import { t, type StringKey } from '../i18n'
import { INK_COLORS, type InkColor, type InkTool } from './types'

/**
 * The tutor's annotation kit, floating over the top of the screen share while
 * Draw is armed.
 *
 * It lives here rather than in the bottom control bar because it is a lot of
 * choices — seven tools, five colours, undo, clear — and cramming those into the
 * class control bar would crowd out the mic and leave controls, especially on a
 * phone. A floating strip over the share is also where a tutor's attention
 * already is. Students never see it (rendered only for the teacher, and only
 * while armed).
 */

interface Props {
  tool: InkTool
  color: InkColor
  onTool: (tool: InkTool) => void
  onColor: (color: InkColor) => void
  onUndo: () => void
  onClear: () => void
}

const TOOLS: { tool: InkTool; icon: string; key: StringKey }[] = [
  { tool: 'pen', icon: '🖊', key: 'surface.pen' },
  { tool: 'highlighter', icon: '🖍', key: 'surface.highlighter' },
  { tool: 'arrow', icon: '↗', key: 'surface.arrow' },
  { tool: 'line', icon: '／', key: 'surface.line' },
  { tool: 'rect', icon: '▭', key: 'surface.rect' },
  { tool: 'ellipse', icon: '◯', key: 'surface.ellipse' },
  { tool: 'eraser', icon: '🩹', key: 'surface.eraser' },
]

export function InkToolbar({ tool, color, onTool, onColor, onUndo, onClear }: Props) {
  return (
    // dir=ltr so the tool order stays stable regardless of the Arabic chrome —
    // these are spatial icons, not reading text.
    <div className="ink-toolbar" dir="ltr" role="toolbar" aria-label={t('surface.tools')}>
      <div className="ink-toolbar__group">
        {TOOLS.map(({ tool: value, icon, key }) => (
          <button
            key={value}
            type="button"
            className={`ink-tool ${tool === value ? 'ink-tool--active' : ''}`}
            onClick={() => onTool(value)}
            aria-pressed={tool === value}
            aria-label={t(key)}
            title={t(key)}
          >
            {icon}
          </button>
        ))}
      </div>

      {/* No colour for the eraser — it removes, it does not draw. */}
      {tool === 'eraser' ? null : (
        <div className="ink-toolbar__group ink-toolbar__colors">
          {INK_COLORS.map((value) => (
            <button
              key={value}
              type="button"
              className={`ink-swatch ${color === value ? 'ink-swatch--active' : ''}`}
              style={{ background: value }}
              onClick={() => onColor(value)}
              aria-pressed={color === value}
              aria-label={t('surface.color')}
              title={t('surface.color')}
            />
          ))}
        </div>
      )}

      <div className="ink-toolbar__group">
        <button type="button" className="ink-tool" onClick={onUndo} aria-label={t('surface.undo')} title={t('surface.undo')}>
          ↩
        </button>
        <button type="button" className="ink-tool" onClick={onClear} aria-label={t('surface.clearInk')} title={t('surface.clearInk')}>
          🧽
        </button>
      </div>
    </div>
  )
}
